import {
  AdminRoles,
  BpAdminRoles,
  type ChildProfilesClaim,
  GroupRoles,
  type OmniOrganizationClaim,
  type OrganizationClaim,
  type OtherProfilesClaim,
  type ProfileClaim,
  ProfileRoles,
  type RolesClaim,
  type UserClaim,
} from '@bp/pim-auth-constants';
import {
  ClaimsPermissionChecker,
  type GroupSubject,
  type OrganizationSubject,
  type ProfileSubject,
} from '@bp/pim-auth-permissions';
import dayjs, { OpUnitType } from 'dayjs';

export interface ProfileRoleSubject extends ProfileSubject {
  role: ProfileRoles;
}

export interface AuthorizationClaims {
  profile: ProfileClaim;
  organization: OrganizationClaim;
  user?: UserClaim;
  roles: RolesClaim;
  otherProfiles?: OtherProfilesClaim;
  childProfiles?: ChildProfilesClaim;
  omniOrganization: OmniOrganizationClaim;
}

export interface WorkmaterialSubject {
  workmaterialUuid: string;
  owner: ProfileSubject;
}

export interface CalendarEventSubject {
  organizationUuid: string;
  ownerProfileUuid: string;
}

export interface FileEntrySubject {
  organization: OrganizationSubject;
  owner: ProfileSubject;
}

export interface ZoomMeetingSubject {
  organizationUuid: string;
  profileUuid: string;
}

export interface AssignmentSubject {
  uuid: string;
  ownerUuid: string;
  groupUuid: string;
  organization: OrganizationSubject;
}

export type ManagedByGroupSubject = GroupSubject & {
  managedBy?: string;
};

export class BpPermissionChecker extends ClaimsPermissionChecker {
  private readonly allowStudentDirectMessages: boolean = true;
  private readonly allowParentExcuses: boolean = false;
  private readonly hasNextcloudUser: boolean = false;
  private otherProfiles?: OtherProfilesClaim;
  private childProfiles?: ChildProfilesClaim;

  constructor(
    claims: AuthorizationClaims,
    allowStudentDirectMessages: boolean = true,
    allowParentExcuses: boolean = false,
    hasNextcloudUser: boolean = false,
  ) {
    super(claims.profile, claims.organization, claims.roles, claims.user, claims.omniOrganization);
    this.otherProfiles = claims.otherProfiles;
    this.childProfiles = claims.childProfiles;
    this.allowStudentDirectMessages = allowStudentDirectMessages;
    this.allowParentExcuses = allowParentExcuses;
    this.hasNextcloudUser = hasNextcloudUser;
  }

  // start PROFILE
  canChangeProfile() {
    return (this.otherProfiles ?? []).length > 0;
  }

  canImpersonateChildProfile() {
    return this.childProfiles?.some((p) => p.canBeImpersonated);
  }

  isLegalAge(_profile?: ProfileClaim) {
    const profile = _profile ?? this.profile;
    if (!profile.birthday) return false;
    const today = dayjs().startOf('day');
    const birthday = dayjs(profile.birthday).startOf('day');
    const diff = today.diff(birthday);
    return diff / 31536000000 >= 18;
  }

  isMemberWithChildren(_organization?: OrganizationSubject, _children?: ChildProfilesClaim) {
    const organization = _organization ?? this.organization;
    const children = _children ?? this.childProfiles;
    return this.isOrganizationMember(organization) && children && children.length > 0;
  }
  // end PROFILE

  // start LICENSE & COLLABORATION
  canCreateCollaboration(_organization: OrganizationSubject) {
    return (
      // disable this for now
      // this.hasOrganizationRole(AdminRoles.OrganizationAdmin, organization) ||
      // this.hasOrganizationRole(AdminRoles.CollaborationAdmin, organization) ||
      this.isOmniAdmin()
    );
  }

  canUpdateBBB(_organization: OrganizationSubject) {
    return this.isOmniAdmin();
  }

  canUpdateBildungsplattform(_organization: OrganizationSubject) {
    return this.isOmniAdmin();
  }

  canUpdateNextcloud(_organization: OrganizationSubject) {
    return this.isOmniAdmin();
  }

  canUpdateThreema(_organization: OrganizationSubject) {
    return this.isOmniAdmin();
  }

  canManageThreema(organization: OrganizationSubject) {
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(organization) ||
      this.hasOrganizationRole(BpAdminRoles.ThreemaAdmin, organization)
    );
  }

  canManageNextcloud(organization: OrganizationSubject) {
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(organization) ||
      this.hasOrganizationRole(BpAdminRoles.CloudAdmin, organization)
    );
  }

  canUpdateThreemaBroadcast(_organization: OrganizationSubject) {
    return this.isOmniAdmin();
  }

  canUpdateZoom(_organization: OrganizationSubject) {
    return this.isOmniAdmin();
  }

  canUpdateCollaborationGroups(organization?: OrganizationSubject) {
    return (
      (this.isOmniAdmin() ||
        this.isOrganizationAdmin(organization ?? this.organization) ||
        this.hasOrganizationRole(AdminRoles.CollaborationAdmin, organization ?? this.organization) ||
        this.hasOrganizationRole(AdminRoles.GroupAdmin, organization ?? this.organization)) &&
      this.organization.collaboratesWith.length > 0
    );
  }

  canUpdateCollaborationCourses(organization?: OrganizationSubject) {
    return this.canUpdateCollaborationGroups(organization);
  }

  // end LICENSE & COLLABORATION

  // start INSTITUTION
  canListInstitutionGroups(organization: OrganizationSubject) {
    return (
      this.isOmniAdmin() ||
      this.hasOrganizationRole(AdminRoles.OrganizationAdmin, organization) ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, organization)
    );
  }

  canListInstitutionCourses(organization: OrganizationSubject) {
    return this.canListInstitutionGroups(organization);
  }

  canAccessProcuratImport(organization?: OrganizationSubject) {
    return (
      this.isOrganizationAdmin(organization ?? this.organization) ||
      this.hasOrganizationRole(BpAdminRoles.ImportAdmin, organization ?? this.organization)
    );
  }

  // end INSTITUTION

  // start CLASSES
  canListClasses(organization: OrganizationSubject) {
    return (
      this.isOmniAdmin() ||
      this.hasOrganizationRole(AdminRoles.OrganizationAdmin, organization) ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, organization)
    );
  }

  // end CLASSES

  // start SUBJECT
  canListSubjects(organization: OrganizationSubject) {
    return this.isOmniAdmin() || this.hasOrganizationRole(AdminRoles.OrganizationAdmin, organization);
  }

  canCreateSubject(organization: OrganizationSubject) {
    return this.canListSubjects(organization);
  }

  canUpdateSubject(organization: OrganizationSubject) {
    return this.canListSubjects(organization);
  }

  canDeleteSubject(_organization: OrganizationSubject) {
    return this.isOmniAdmin();
  }

  // end SUBJECT

  // start SCHOOLYEAR
  canListSchoolyears(organization: OrganizationSubject) {
    return (
      this.isOmniAdmin() ||
      this.hasOrganizationRole(AdminRoles.OrganizationAdmin, organization) ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin)
    );
  }

  canCreateSchoolyear(organization: OrganizationSubject) {
    return this.canListSchoolyears(organization);
  }

  canUpdateSchoolyear(organization: OrganizationSubject) {
    return this.canListSchoolyears(organization);
  }

  canDeleteSchoolyear(organization: OrganizationSubject) {
    return this.canListSchoolyears(organization);
  }

  // end SCHOOLYEAR

  // start ANNOUNCEMENT
  canCreateGroupAnnouncement(group: GroupSubject) {
    return (
      this.isOmniAdmin() || this.hasGroupRole(GroupRoles.Editor, group) || this.hasGroupRole(GroupRoles.Admin, group)
    );
  }

  canCreateCourseAnnouncement(group: GroupSubject) {
    return this.canCreateGroupAnnouncement(group);
  }

  canCreateOrganizationAnnouncment(organization: OrganizationSubject): boolean {
    return this.isOrganizationAdmin(organization) || this.isOmniAdmin();
  }

  // end ANNOUNCEMENT

  // start ASSIGNMENT
  canCreateAssignment(group: GroupSubject) {
    return this.isOmniAdmin() || this.hasGroupRole(GroupRoles.Editor, group);
  }

  canUpdateAssignment(assignment: AssignmentSubject) {
    return (
      this.isOmniAdmin() ||
      this.is({
        uuid: assignment.ownerUuid,
        organization: assignment.organization,
      }) ||
      this.hasGroupRole(GroupRoles.Editor, {
        uuid: assignment.groupUuid,
        organization: assignment.organization,
      })
    );
  }

  canDeleteAssignment(assignment: AssignmentSubject) {
    return (
      this.isOmniAdmin() ||
      this.is({
        uuid: assignment.ownerUuid,
        organization: assignment.organization,
      }) ||
      this.hasGroupRole(GroupRoles.Editor, {
        uuid: assignment.groupUuid,
        organization: assignment.organization,
      })
    );
  }

  // end ASSIGNMENT

  // start SUBMISSION
  canViewSubmissions(assignment: AssignmentSubject) {
    return (
      this.isOmniAdmin() ||
      this.is({
        uuid: assignment.ownerUuid,
        organization: assignment.organization,
      }) ||
      this.hasGroupRole(GroupRoles.Editor, {
        uuid: assignment.groupUuid,
        organization: assignment.organization,
      }) ||
      this.hasGroupRole(GroupRoles.Admin, {
        uuid: assignment.groupUuid,
        organization: assignment.organization,
      })
    );
  }

  // end SUBMISSION

  // start ZOOM
  canReadZoomOrganization(organization: OrganizationSubject): boolean {
    return this.isOrganizationAdmin(organization) || this.isOmniAdmin();
  }

  canCreateZoomOrganization(organization: OrganizationSubject): boolean {
    return this.isOrganizationAdmin(organization) || this.isOmniAdmin();
  }

  canUpdateZoomOrganizationSettings(organization: OrganizationSubject): boolean {
    return this.isOrganizationAdmin(organization) || this.isOmniAdmin();
  }

  canUpdateZoomOrganizationCredentials(organization: OrganizationSubject): boolean {
    return this.isOrganizationAdmin(organization) || this.isOmniAdmin();
  }

  canDeleteZoomOrganization(organization: OrganizationSubject): boolean {
    return this.isOrganizationAdmin(organization) || this.isOmniAdmin();
  }

  canReadZoomProfile(profile: ProfileSubject): boolean {
    // TODO allowed if given profile is current profile?
    return this.isOrganizationAdmin(profile.organization) || this.isOmniAdmin();
  }

  canCreateZoomProfile(organization: OrganizationSubject): boolean {
    return this.isOrganizationAdmin(organization) || this.isOmniAdmin();
  }

  canDeleteZoomProfile(profile: ProfileSubject): boolean {
    return this.isOrganizationAdmin(profile.organization) || this.isOmniAdmin();
  }

  canListZoomProfiles(organization: OrganizationSubject): boolean {
    return this.isOrganizationAdmin(organization) || this.isOmniAdmin();
  }

  canCreateZoomMeeting(organization: OrganizationSubject): boolean {
    return this.isOrganizationAdmin(organization) || this.isOmniAdmin();
  }

  canReadZoomMeeting(zoomMeeting: ZoomMeetingSubject): boolean {
    // TODO allowed if given profile is meeting user?
    return this.isOrganizationAdmin({ uuid: zoomMeeting.organizationUuid }) || this.isOmniAdmin();
  }

  canListZoomMeetings(organization: OrganizationSubject): boolean {
    return this.isOrganizationAdmin(organization) || this.isOmniAdmin();
  }

  // end ZOOM

  // start PROFILE
  canListProfiles(organization: OrganizationSubject) {
    return (
      this.hasOrganizationRole(AdminRoles.ProfileAdmin, organization) ||
      this.isOrganizationAdmin(organization) ||
      this.isOmniAdmin()
    );
  }

  // end PROFILE

  // start FILE
  canUploadFile(_organization: OrganizationSubject): boolean {
    // TODO implement
    warnAboutInsufficientPermissionChecks();
    return true;
  }

  canReadFileEntry(fileEntry: FileEntrySubject): boolean {
    if (this.isOmniAdmin() || this.isOrganizationAdmin({ uuid: fileEntry.organization.uuid })) {
      return true;
    }

    if (fileEntry.organization.uuid !== this.profile.organization.uuid) {
      return false;
    }

    if (fileEntry.owner.uuid === this.profile.uuid) {
      return true;
    }

    // TODO implement more checks. A file can be read if it is made public to the current profile somehow
    // For now, allow access to all profiles in the same organization
    warnAboutInsufficientPermissionChecks();

    return true;
  }

  canDownloadFile(fileEntry: FileEntrySubject): boolean {
    return this.canReadFileEntry(fileEntry);
  }

  // end FILE

  // start GROUP
  isGroupAdmin(group: GroupSubject) {
    return this.isOmniAdmin() || this.hasGroupRole(GroupRoles.Admin, group);
  }

  isGroupEditor(group: GroupSubject) {
    return this.isOmniAdmin() || this.hasGroupRole(GroupRoles.Editor, group);
  }

  isGroupViewer(group: GroupSubject) {
    return this.isOmniAdmin() || this.hasGroupRole(GroupRoles.Viewer, group);
  }

  isGroupMember(group: GroupSubject) {
    return this.isGroupEditor(group) || this.isGroupViewer(group);
  }

  canViewGroup(group: GroupSubject) {
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(this.organization) ||
      this.isGroupAdmin(group) ||
      this.isGroupMember(group)
    );
  }

  canCreateUserInGroup(group: GroupSubject) {
    return this.isOmniAdmin() || this.isGroupAdmin(group);
  }

  canCreateCollaborationGroups() {
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(this.organization) ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, this.organization)
    );
  }

  canCreateBpGroups() {
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(this.organization) ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, this.organization) ||
      this.hasOrganizationRole(ProfileRoles.Teacher, this.organization)
    );
  }

  canEditBpGroup(group: GroupSubject) {
    return (
      this.isOmniAdmin() ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, this.organization) ||
      this.isGroupAdmin(group)
    );
  }

  canDeleteBpGroups(group?: GroupSubject & ManagedByGroupSubject) {
    return (
      group?.organization.uuid === this.organization.uuid &&
      group?.managedBy === 'bp' &&
      (this.isOmniAdmin() ||
        this.hasOrganizationRole(AdminRoles.GroupAdmin, this.organization) ||
        this.isGroupAdmin(group))
    );
  }

  canCreateBpGroupMaterial(group: GroupSubject) {
    return this.canEditBpGroup(group) || this.isGroupEditor(group);
  }

  canCreateBpGroupNotifications(group: GroupSubject) {
    return this.canCreateBpGroups() || this.canEditBpGroup(group) || this.isGroupEditor(group);
  }

  canCreateBpGroupEvents(group: GroupSubject) {
    return this.canEditBpGroup(group) || this.isGroupEditor(group);
  }

  canCreateBpCourseEvents(group: GroupSubject) {
    return this.canEditBpGroup(group) || this.isGroupEditor(group);
  }

  canUpdateEvents(event: CalendarEventSubject) {
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin({ uuid: event.organizationUuid }) ||
      this.is({ uuid: event.ownerProfileUuid, organization: { uuid: event.organizationUuid } })
    );
  }

  canChangeBpGroupPermissions(group: GroupSubject) {
    return (
      this.isGroupAdmin(group) ||
      this.isGroupEditor(group) ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, group.organization) ||
      this.isOmniAdmin()
    );
  }

  canChangeBpCoursePermissions(group: GroupSubject) {
    return (
      this.isGroupAdmin(group) ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, group.organization) ||
      this.isOmniAdmin()
    );
  }

  // end GROUP

  // start COURSE
  canViewCourse(group: GroupSubject) {
    return this.canViewGroup(group);
  }

  canCreateUserInCourse(group: GroupSubject) {
    return this.isOmniAdmin() || this.isGroupAdmin(group) || this.isGroupEditor(group);
  }

  canDownloadClasslist(group: GroupSubject): boolean {
    return this.isOmniAdmin() || this.isGroupEditor(group);
  }

  canUpdateGroup(group: ManagedByGroupSubject): boolean {
    return group?.managedBy === 'bp' && super.canUpdateGroup(group);
  }

  canDeleteGroup(group: ManagedByGroupSubject): boolean {
    return group?.managedBy === 'bp' && super.canDeleteGroup(group);
  }

  canCreateCourse() {
    return (
      this.isOmniAdmin() ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, this.organization) ||
      this.hasOrganizationRole(ProfileRoles.Teacher, this.organization)
    );
  }

  canDeleteCourse(group?: GroupSubject & ManagedByGroupSubject) {
    return (
      group?.organization.uuid === this.organization.uuid &&
      group?.managedBy === 'bp' &&
      (this.isOmniAdmin() ||
        this.hasOrganizationRole(AdminRoles.GroupAdmin, this.organization) ||
        this.isGroupAdmin(group))
    );
  }

  canUpdateCourse(group: ManagedByGroupSubject) {
    return (
      group.managedBy === 'bp' &&
      (this.isOmniAdmin() ||
        this.hasOrganizationRole(AdminRoles.GroupAdmin, this.organization) ||
        this.isGroupAdmin(group))
    );
  }

  isCourseEditor(group: GroupSubject) {
    return this.hasGroupRole(GroupRoles.Editor, group);
  }

  canUpdateGroupSettings(group: GroupSubject) {
    return (
      this.isOmniAdmin() ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, this.organization) ||
      this.isGroupAdmin(group)
    );
  }

  canCreateWorkmaterial(group: GroupSubject) {
    return (
      this.isOmniAdmin() ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, this.organization) ||
      this.isGroupEditor(group) ||
      this.isGroupAdmin(group)
    );
  }

  canDeleteWorkmaterial(group: GroupSubject, workmaterial: WorkmaterialSubject) {
    return (
      this.isOmniAdmin() ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, this.organization) ||
      this.isGroupAdmin(group) ||
      this.is(workmaterial.owner) ||
      this.isGroupEditor(group)
    );
  }

  canUpdateWorkmaterial(group: GroupSubject, workmaterial: WorkmaterialSubject) {
    return this.isOmniAdmin() || this.is(workmaterial.owner) || this.isGroupEditor(group);
  }

  // end COURSE

  // start MATRIX
  canDirectMessageWithinGroup(group: GroupSubject, target: ProfileRoleSubject): boolean {
    return (
      this.isOmniAdmin() ||
      this.isGroupAdmin(group) ||
      this.isOrganizationAdmin(this.organization) ||
      !this.hasOrganizationRole(ProfileRoles.Student, this.organization) ||
      (this.hasOrganizationRole(ProfileRoles.Student, this.organization) &&
        (this.allowStudentDirectMessages || target.role !== ProfileRoles.Student))
    );
  }

  canDirectMessageWithinCourse(group: GroupSubject, target: ProfileRoleSubject): boolean {
    return this.canDirectMessageWithinGroup(group, target);
  }

  // end MATRIX

  // start SETTINGS
  canViewSettings(organization: OrganizationSubject) {
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(organization) ||
      this.hasOrganizationRole(AdminRoles.CollaborationAdmin, organization) ||
      this.hasOrganizationRole(AdminRoles.GroupAdmin, organization) ||
      this.hasOrganizationRole(AdminRoles.ProfileAdmin, organization)
    );
  }

  // end SETTINGS

  // start CLASSBOOK
  canViewClassbook(organization: OrganizationSubject) {
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(organization) ||
      this.hasOrganizationRole(ProfileRoles.Teacher, organization)
    );
  }
  // end CLASSBOOK

  /* -------------------------------------------------------------------------- */
  /*                                  ABSENCES                                  */
  /* -------------------------------------------------------------------------- */
  private isAbsenceDateValid(date?: Date, unit: OpUnitType = 'minute') {
    if (!date) {
      // no date given -> bypass
      return true;
    } else if (!(date instanceof Date) || isNaN(date.getTime()) || !dayjs(date).isValid()) {
      // date is invalid
      return false;
    } else {
      // date is valid if same or in future
      return dayjs(date).startOf(unit).isSameOrAfter(dayjs().startOf(unit));
    }
  }

  canViewAbsences(data?: { organization?: OrganizationClaim; children?: ChildProfilesClaim }) {
    const organization = data?.organization ?? this.organization;
    const children = data?.children ?? this.childProfiles;
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(organization) ||
      this.hasOrganizationRole(ProfileRoles.Student, organization) ||
      this.hasOrganizationRole(ProfileRoles.Teacher, organization) ||
      this.hasOrganizationRole(BpAdminRoles.SchoolOffice, organization) ||
      this.isMemberWithChildren(organization, children)
    );
  }

  canViewOwnAbsences(data?: { organization?: OrganizationClaim }) {
    const organization = data?.organization ?? this.organization;
    return (
      this.isOmniAdmin() ||
      this.hasOrganizationRole(ProfileRoles.Student, organization) ||
      this.isMemberWithChildren(organization)
    );
  }

  canViewOwnAbsencesOnly(data?: { organization?: OrganizationClaim }) {
    const organization = data?.organization ?? this.organization;
    return (
      this.canViewOwnAbsences({ organization }) &&
      !this.canViewAbsencesAdministration({ organization }) &&
      !this.canViewAbsencesEvaluation({ organization })
    );
  }

  /* ------------------------------ SCHÜLER:INNEN ----------------------------- */
  canCreateAbsenceForSelf(data?: { organization?: OrganizationSubject; profile?: ProfileClaim; start?: Date }) {
    const organization = data?.organization ?? this.organization;
    const profile = data?.profile ?? this.profile;
    return (
      this.isOmniAdmin() ||
      (this.hasOrganizationRole(ProfileRoles.Student, organization) &&
        this.isLegalAge(profile) &&
        this.isAbsenceDateValid(data?.start))
    );
  }

  canUpdateAbsenceForSelf(data?: { organization?: OrganizationSubject; profile?: ProfileClaim; start?: Date }) {
    const organization = data?.organization ?? this.organization;
    const profile = data?.profile ?? this.profile;
    return (
      this.isOmniAdmin() ||
      (this.hasOrganizationRole(ProfileRoles.Student, organization) &&
        this.isLegalAge(profile) &&
        this.isAbsenceDateValid(data?.start))
    );
  }

  canDeleteAbsenceForSelf(data?: { organization?: OrganizationSubject; profile?: ProfileClaim; start?: Date }) {
    const organization = data?.organization ?? this.organization;
    const profile = data?.profile ?? this.profile;
    return (
      this.isOmniAdmin() ||
      (this.hasOrganizationRole(ProfileRoles.Student, organization) &&
        this.isLegalAge(profile) &&
        this.isAbsenceDateValid(data?.start))
    );
  }

  canExcuseSelf() {
    return false; // for future use
  }

  /* --------------------------------- ELTERN --------------------------------- */
  canCreateAbsenceForChildren(data?: { organization?: OrganizationSubject; children?: ChildProfilesClaim }) {
    const organization = data?.organization ?? this.organization;
    const children = data?.children ?? this.childProfiles;
    return this.isOmniAdmin() || this.isMemberWithChildren(organization, children);
  }

  canUpdateAbsenceForChildren(data?: {
    organization?: OrganizationSubject;
    children?: ChildProfilesClaim;
    end?: Date;
  }) {
    const organization = data?.organization ?? this.organization;
    const children = data?.children ?? this.childProfiles;

    return (
      this.isOmniAdmin() ||
      (this.isMemberWithChildren(organization, children) && this.isAbsenceDateValid(data?.end, 'day'))
    );
  }

  canDeleteAbsenceForChildren(data?: {
    organization?: OrganizationSubject;
    children?: ChildProfilesClaim;
    start?: Date;
  }) {
    const organization = data?.organization ?? this.organization;
    const children = data?.children ?? this.childProfiles;
    return (
      this.isOmniAdmin() || (this.isMemberWithChildren(organization, children) && this.isAbsenceDateValid(data?.start))
    );
  }

  canExcuseChildren(data?: {
    organization?: OrganizationSubject;
    children?: ChildProfilesClaim;
    allowParentExcuses?: boolean;
  }) {
    const organization = data?.organization ?? this.organization;
    const children = data?.children ?? this.childProfiles;
    const allowParentExcuses = data?.allowParentExcuses ?? this.allowParentExcuses;
    return this.isOmniAdmin() || (this.isMemberWithChildren(organization, children) && allowParentExcuses);
  }

  /* ------------------------------- SEKRETARIAT ------------------------------ */
  canViewAbsencesAdministration(data?: { organization?: OrganizationSubject }) {
    const organization = data?.organization ?? this.organization;
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(organization) ||
      this.hasOrganizationRole(ProfileRoles.Teacher, organization) ||
      this.hasOrganizationRole(BpAdminRoles.SchoolOffice, organization)
    );
  }

  canEditAdministrationAbsence(data?: { organization?: OrganizationSubject }) {
    const organization = data?.organization ?? this.organization;
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(organization) ||
      this.hasOrganizationRole(ProfileRoles.Teacher, organization) ||
      this.hasOrganizationRole(BpAdminRoles.SchoolOffice, organization)
    );
  }

  canUpdateAdministrationAbsence(data?: { organization?: OrganizationSubject }) {
    const organization = data?.organization ?? this.organization;
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(organization) ||
      this.hasOrganizationRole(ProfileRoles.Teacher, organization) ||
      this.hasOrganizationRole(BpAdminRoles.SchoolOffice, organization)
    );
  }

  canDeleteAdministrationAbsence(data?: { organization?: OrganizationSubject }) {
    const organization = data?.organization ?? this.organization;
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(organization) ||
      this.hasOrganizationRole(ProfileRoles.Teacher, organization) ||
      this.hasOrganizationRole(BpAdminRoles.SchoolOffice, organization)
    );
  }

  canViewAllAbsences(data?: { organization?: OrganizationSubject }) {
    const organization = data?.organization ?? this.organization;
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(organization) ||
      this.hasOrganizationRole(BpAdminRoles.SchoolOffice, organization)
    );
  }

  canExcuseAdministrationAbsence() {
    return true; // for future use
  }

  /* ------------------------------- AUSWERTUNG ------------------------------- */
  canViewAbsencesEvaluation(data?: { organization?: OrganizationSubject }) {
    const organization = data?.organization ?? this.organization;
    return (
      this.isOmniAdmin() ||
      this.isOrganizationAdmin(organization) ||
      this.hasOrganizationRole(ProfileRoles.Teacher, organization) ||
      this.hasOrganizationRole(BpAdminRoles.SchoolOffice, organization)
    );
  }

  // start MEETINGS
  canViewMeetings(organization: OrganizationSubject) {
    return this.isOmniAdmin() || this.isOrganizationAdmin(organization);
  }

  canEndMeetings({ organization, ownerUuid }: { organization: OrganizationSubject; ownerUuid: string }) {
    return this.isOmniAdmin() || this.isOrganizationAdmin(organization) || this.profile.uuid === ownerUuid;
  }

  canAccessMeetingAsModerator(_event: Event) {
    return this.isOmniAdmin() || this.isOrganizationAdmin(this.organization);
  }

  canAccessMeetingAsParticipant(_group: GroupSubject) {
    return this.isOmniAdmin() || this.isOrganizationAdmin(this.organization);
  }

  // end MEETINGS

  /* -------------------------------------------------------------------------- */
  /*                                HAUSAUFGABEN;                               */
  /* -------------------------------------------------------------------------- */
  canViewHomeworks(organization: OrganizationSubject) {
    return (
      this.isOmniAdmin() ||
      this.isMemberWithChildren(organization) ||
      this.hasOrganizationRole(ProfileRoles.Student, organization)
    );
  }

  // start TEMPLATES
  canViewTemplates() {
    return this.isOmniAdmin();
  }

  // end TEMPLATES

  // start MEDIA
  canViewMediaLibrary() {
    return this.isOmniAdmin();
  }

  // end MEDIA

  // start ADMIN
  canViewAdmin() {
    return this.isOmniAdmin();
  }

  // end ADMIN

  // start CLOUD
  canAccessCloud() {
    return this.hasNextcloudUser;
  }

  // end CLOUD

  // start Imports
  canImport(organization: OrganizationSubject) {
    return this.isOmniAdmin() || this.hasOrganizationRole(BpAdminRoles.ImportAdmin, organization);
  }

  // end Imports
}

function warnAboutInsufficientPermissionChecks() {
  console.log('FIXME: Insufficient permission checks in %s', __filename);
}
